import { useState, useCallback, useRef, createContext, useContext } from 'react'

// ── Context (para usarlo desde cualquier página sin pasar props) ──────────────
const ToastContext = createContext(null)

export function ToastProvider({ children }) {
  const { toast, toasts, dismiss } = _useToastState()
  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  )
}

// Llama esto en tus páginas: const toast = useToast()
export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast debe usarse dentro de <ToastProvider>')
  return ctx
}

// ── Hook interno (estado real) ────────────────────────────────────────────────
function _useToastState() {
  const [toasts, setToasts] = useState([])
  const timers = useRef({})

  const dismiss = useCallback((id) => {
    clearTimeout(timers.current[id])
    setToasts(prev =>
      prev.map(t => (t.id === id ? { ...t, leaving: true } : t))
    )
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 350)
  }, [])

  const toast = useCallback(({ type = 'info', title, message, duration = 4000 }) => {
    const id = Date.now() + Math.random()
    setToasts(prev => [...prev, { id, type, title, message, leaving: false }])
    timers.current[id] = setTimeout(() => dismiss(id), duration)
    return id
  }, [dismiss])

  // Helpers
  toast.success = (title, message, opts) => toast({ type: 'success', title, message, ...opts })
  toast.error   = (title, message, opts) => toast({ type: 'error',   title, message, ...opts })
  toast.info    = (title, message, opts) => toast({ type: 'info',    title, message, ...opts })
  toast.warning = (title, message, opts) => toast({ type: 'warning', title, message, ...opts })

  return { toast, toasts, dismiss }
}

// ── Renderer ─────────────────────────────────────────────────────────────────
const ICONS = {
  success: '✅',
  error:   '❌',
  info:    'ℹ️',
  warning: '⚠️',
}

const COLORS = {
  success: { bar: 'var(--color-success, #22c55e)',  bg: 'rgba(34,197,94,0.08)',  border: 'rgba(34,197,94,0.25)'  },
  error:   { bar: 'var(--color-danger, #ef4444)',   bg: 'rgba(239,68,68,0.08)',  border: 'rgba(239,68,68,0.25)'  },
  info:    { bar: 'var(--color-primary, #22c55e)',  bg: 'var(--color-primary-lt, rgba(34,197,94,0.10))', border: 'var(--color-border, #2a2d35)' },
  warning: { bar: 'var(--color-warning, #f59e0b)',  bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.25)' },
}

export function ToastContainer({ toasts, dismiss }) {
  if (!toasts.length) return null

  return (
    <>
      <style>{`
        @keyframes _toastIn  { from { opacity:0; transform:translateX(100%) scale(0.95) } to { opacity:1; transform:translateX(0) scale(1) } }
        @keyframes _toastOut { from { opacity:1; transform:translateX(0) scale(1) }       to { opacity:0; transform:translateX(100%) scale(0.95) } }
        @keyframes _progress { from { width:100% } to { width:0% } }
        ._toast-item { animation: _toastIn 0.32s cubic-bezier(0.34,1.56,0.64,1) forwards }
        ._toast-item.leaving { animation: _toastOut 0.28s ease forwards }
        ._toast-close { opacity:0; transition: opacity 0.2s }
        ._toast-item:hover ._toast-close { opacity:1 }
      `}</style>

      <div style={{
        position: 'fixed',
        bottom: 24,
        right: 20,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        maxWidth: 360,
        width: 'calc(100vw - 40px)',
        pointerEvents: 'none',
      }}>
        {toasts.map(t => {
          const c = COLORS[t.type] || COLORS.info
          return (
            <div
              key={t.id}
              className={`_toast-item${t.leaving ? ' leaving' : ''}`}
              style={{
                background: 'var(--color-surface, #17181c)',
                border: `1px solid ${c.border}`,
                borderRadius: 18,
                overflow: 'hidden',
                pointerEvents: 'all',
                position: 'relative',
                boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
              }}
            >
              {/* Barra lateral de color */}
              <div style={{
                position: 'absolute',
                left: 0, top: 0, bottom: 0,
                width: 4,
                background: c.bar,
                borderRadius: '18px 0 0 18px',
              }} />

              {/* Contenido */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '14px 16px 14px 20px',
                background: c.bg,
              }}>
                <span style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>
                  {ICONS[t.type]}
                </span>

                <div style={{ flex: 1, minWidth: 0 }}>
                  {t.title && (
                    <div style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: 'var(--color-text, #fff)',
                      marginBottom: t.message ? 3 : 0,
                      lineHeight: 1.3,
                    }}>
                      {t.title}
                    </div>
                  )}
                  {t.message && (
                    <div style={{
                      fontSize: 13,
                      color: 'var(--color-muted, #9ca3af)',
                      lineHeight: 1.5,
                    }}>
                      {t.message}
                    </div>
                  )}
                </div>

                {/* Botón cerrar */}
                <button
                  className="_toast-close"
                  onClick={() => dismiss(t.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-muted, #9ca3af)',
                    fontSize: 18,
                    lineHeight: 1,
                    cursor: 'pointer',
                    flexShrink: 0,
                    padding: '2px 4px',
                    borderRadius: 6,
                    marginTop: -2,
                  }}
                >×</button>
              </div>

              {/* Barra de progreso */}
              {!t.leaving && (
                <div style={{
                  height: 2,
                  background: 'var(--color-border, #2a2d35)',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%',
                    background: c.bar,
                    animation: `_progress ${4000}ms linear forwards`,
                    opacity: 0.7,
                  }} />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </>
  )
}